import type { Client, Exchange, Operation } from '@urql/core';
import { createClient, fetchExchange, makeOperation } from '@urql/core';
import { devtoolsExchange } from '@urql/devtools';
import { authExchange } from '@urql/exchange-auth';
import { cacheExchange } from '@urql/exchange-graphcache';
import { pipe, map } from 'wonka';
import { isRef, ref, unref } from 'vue';
import merge from 'lodash/merge';
import type { AuthResult, GraphCacheConfig } from '~/graphql';
import { RefreshTokenDocument } from '~/graphql';
import { emailNotificationSubscriptionCacheExchange } from '~/stores/emailNotificationSubscriptionStore';
import { itemCacheExchange } from '~/stores/itemManagement';
import { useSession } from '~/stores/session';
import { topicInstancesCacheExchange } from '~/stores/topicInstanceManagement';
import { topicsCacheExchange } from '~/stores/topicManagement';
import { weeklyPlanningManagementCacheExchange } from '~/stores/weeklyPlanningManagement';

import introspection from '~/graphql/__generated__/introspection';
import { courseAiSettingCacheExchange } from '~/stores/courseAiSettingManagement';
import { courseCacheExchange } from '~/stores/courseManagement';
import { apiUrl } from '~/utils/constants';

const graphqlEndpoint = `${apiUrl}/graphql`;

function sanitizeReactiveValue(value: unknown, seen = new WeakMap()): unknown {
  const plainValue = isRef(value) ? unref(value) : value;

  if (
    plainValue === null ||
    plainValue === undefined ||
    typeof plainValue !== 'object'
  ) {
    return plainValue;
  }

  if (plainValue instanceof Date) {
    return plainValue;
  }

  if (seen.has(plainValue)) {
    return seen.get(plainValue);
  }

  if (Array.isArray(plainValue)) {
    const sanitizedArray: unknown[] = [];
    seen.set(plainValue, sanitizedArray);
    for (const entry of plainValue) {
      sanitizedArray.push(sanitizeReactiveValue(entry, seen));
    }
    return sanitizedArray;
  }

  const sanitizedObject: Record<string, unknown> = {};
  seen.set(plainValue, sanitizedObject);

  for (const [key, entry] of Object.entries(plainValue)) {
    sanitizedObject[key] = sanitizeReactiveValue(entry, seen);
  }

  return sanitizedObject;
}

const sanitizeOperationForDevtoolsExchange: Exchange =
  ({ forward }) =>
  (ops$) =>
    pipe(
      ops$,
      map((operation: Operation) =>
        makeOperation(
          operation.kind,
          {
            key: operation.key,
            query: operation.query,
            variables: sanitizeReactiveValue(operation.variables) as Operation['variables'],
          },
          {
            ...operation.context,
            meta: sanitizeReactiveValue(operation.context.meta),
          },
        ),
      ),
      forward,
    );

export function formatBearerToken(token: string) {
  return `Bearer ${token}`;
}

export function deleteTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
}

function getTokens() {
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');
  return { token, refreshToken };
}

export function storeTokens({ access_token, refresh_token }: AuthResult) {
  localStorage.setItem('token', formatBearerToken(access_token));
  localStorage.setItem('refreshToken', refresh_token);
  return {
    token: formatBearerToken(access_token),
    refresh_token,
  };
}

export default defineNuxtPlugin((nuxtApp) => {
  const authFreeEndpoints = [
    'login',
    'signup',
    'emailSignup',
    'publicCourse',
    'publicCourseCalendarDays',
    'publicMaterial',
    'publicItemInstance',
    'itemForItemInstance',
    'publicItemInstances',
    'publicItem',
    'publicTopic',
    'publicTopicInstances',
    'courseInvitePreview',
    'integrationCheck',
    'connectExternalIntegration',
    'requestUserPasswordReset',
    'verifyUserPasswordReset',
    'tenantInvitePreview',
    'aiToolTextToSpeech',
    'ssoSignup',
    'studentAssignmentOpenQuestionHistory',
    'publicCourseFolders',
    'publicCourseFeatures',
    'publicMaterialAnnotationToolAccess',
  ];
  // use urql graphcache
  const cacheConfig: GraphCacheConfig = {
    schema: introspection,
    keys: {
      UserResult: () => null,
      TopicInstanceResult: () => null,
      UserCoursePermission: () => null,
      UserTenantPermission: () => null,
      UserTopicPermission: () => null,
      UserItemPermission: () => null,
      TopicInstanceStatistics: () => null,
      CourseContentVisibility: () => null,
      GlobalConfiguration: () => null,
      TopicStatistics: () => null,
      Configuration: () => null,
      Feature: () => null,
      UserEmailNotificationSubscription: () => null,
      ExternalCoursesResult: () => null,
      UnmappedCourse: () => null,
      ConnectedCourse: () => null,
      TimetablePeriod: () => null,
      TimetableGridEntry: () => null,
      Timetable: () => null,
      FeatureFlag: () => null,
      TimetableDayResult: () => null,
      TimetableDayError: () => null,
      LibraryMetaResult: () => null,
      SupportTokenResult: () => null,
      TopicContent: () => null,
      ItemContent: () => null,
      MaterialContentHtml: () => null,
      MaterialContentStatistics: () => null,
      LibraryNewestContentResult: () => null,
      AssignmentStatus: () => null,
      MaterialInteractiveAssignmentsStudentOverview: () => null,
      AssignmentOpenQuestionHistory: () => null,
      MaterialInteractiveAssignmentStatus: () => null,
      StudentAssignmentOpenQuestionHistory: () => null,
      AssignmentOpenQuestionAttempt: () => null,
      MaterialAnnotationToolAccess: () => null,
    },
    ...merge(
      topicInstancesCacheExchange,
      itemCacheExchange,
      weeklyPlanningManagementCacheExchange,
      emailNotificationSubscriptionCacheExchange,
      courseAiSettingCacheExchange,
      topicsCacheExchange,
      courseCacheExchange,
    ),
  };

  const urqlClient: Client = createClient({
    url: graphqlEndpoint,
    // set default request policy
    requestPolicy: 'cache-and-network',
    // Force all requests to use POST method
    preferGetMethod: false,
    fetchOptions: {
      method: 'POST',
    },
    exchanges: [
      sanitizeOperationForDevtoolsExchange,
      devtoolsExchange,
      /* debugExchange,*/
      cacheExchange(cacheConfig),
      authExchange(async (utils) => {
        let { token, refreshToken } = getTokens();
        let refreshCallCount = 0;
        const session = useSession();
        return {
          didAuthError(error, operation) {
            return error.graphQLErrors.some(
              (e) => e.extensions.code === 'UNAUTHENTICATED',
            );
          },
          willAuthError(operation) {
            // https://formidable.com/open-source/urql/docs/advanced/authentication/#configuring-willautherror
            token = localStorage.getItem('token');
            if (!token) {
              // this will only run if the token isn't true
              const innerVals = [];
              const val = !operation.query.definitions.some(
                function (definition) {
                  return (
                    definition.kind === 'OperationDefinition' &&
                    definition.selectionSet.selections.some((node) => {
                      innerVals.push(node.name.val);
                        console.log(node.name.value)
                      return (
                        node.kind === 'Field' &&
                        authFreeEndpoints.includes(node.name.value)
                      );
                    })
                  );
                },
              );
              return val;
              // if the operation is not one of the auth free endpoints it will result in an error
            }
            //TODO EVALUATE EXPIRATION DIRECTLY (Return true if either of the tokens have expired)
            return false;
          },
          addAuthToOperation(operation) {
            if (!token) {
              return operation;
            }

            const tokenString = token.includes('Bearer')
              ? token
              : formatBearerToken(token);

            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: tokenString,
                },
              },
            });
          },
          async refreshAuth() {
            // called when auth error has occurred
            // we update refresh the token with a GraphQL mutation
            if (refreshCallCount > 3) {
              session.logOutAndRedirect();
              return;
            }

            refreshCallCount++;

            try {
              refreshToken = localStorage.getItem('refreshToken');

              const result = await utils.mutate(RefreshTokenDocument, {
                refreshTokenInput: {
                  refreshToken,
                },
              });

              if (result.data?.refreshToken) {
                token = result.data.refreshToken.access_token;
                refreshToken = result.data.refreshToken.refresh_token;
                storeTokens(result.data.refreshToken);
                refreshCallCount = 0;
              } else {
                session.logOutAndRedirect();
                return;
              }
            } catch (e) {
              session.logOutAndRedirect();
              console.log('Error refreshing token');
              console.log(e);
            }
          },
        };
      }),
      fetchExchange,
    ],
  });

  nuxtApp.provide('urql', urqlClient);
  const { vueApp } = nuxtApp;
  vueApp.provide('$urql', ref(urqlClient));
});
